import { defineStore } from "pinia";

export type ThemePreference = "light" | "dark" | "system";

const STORAGE_KEY = "axiom-theme";

function systemPrefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function applyTheme(pref: ThemePreference): void {
  const dark = pref === "dark" || (pref === "system" && systemPrefersDark());
  document.documentElement.classList.toggle("dark", dark);
}

/** Manual light/dark/system theme toggle, persisted in localStorage. */
export const useThemeStore = defineStore("theme", {
  state: () => ({
    preference: (localStorage.getItem(STORAGE_KEY) as ThemePreference | null) ?? "system",
    /**
     * Whether dark is in effect RIGHT NOW, with "system" already resolved.
     *
     * Tracked as reactive state (not just as a class on <html>) because the
     * SVG canvas cannot use Tailwind's `dark:` variant on a `fill` attribute.
     * Blocks used to work around that by rendering their background rect
     * TWICE — once light, once dark, one hidden by CSS — which doubled the
     * rect and <title> nodes for every claim on the canvas. Knowing the
     * resolved theme in JS lets a single rect pick its own fill.
     */
    dark: false,
  }),
  actions: {
    init(): void {
      applyTheme(this.preference);
      this.dark = document.documentElement.classList.contains("dark");
      window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
        if (this.preference === "system") {
          applyTheme(this.preference);
          this.dark = document.documentElement.classList.contains("dark");
        }
      });
    },
    setPreference(pref: ThemePreference): void {
      this.preference = pref;
      localStorage.setItem(STORAGE_KEY, pref);
      applyTheme(pref);
      this.dark = document.documentElement.classList.contains("dark");
    },
    cycle(): void {
      const order: ThemePreference[] = ["light", "dark", "system"];
      const next = order[(order.indexOf(this.preference) + 1) % order.length];
      this.setPreference(next);
    },
  },
});
